/**
 * 插件工作流内联执行时，补齐与各业务模块相同的数据落点（裁剪库、抠图图片空间等），
 * 便于回到「图案裁剪 / 智能抠图」详情或列表时数据一致。
 */
import { cropImageAPI, imageSpaceAPI } from '@/utils/api';
import { ensureImageSpaceItemsServerUrl, isPersistedImageUrl, resolveSignedDisplayUrl } from '@/utils/imageUrlUtils';
import { sanitizeManagedDisplayResult } from '@/utils/imageDisplayAccess';
import { workbenchPreviewUrl } from '@/utils/workbenchImagePreview';
import { compressImageForDatabase, ensureImageSizeWithinLimit } from '@/utils/imageUpload';
import { saveImageToIndexedDB } from '@/utils/imageStorage';

function loadImageDimensions(url) {
  return new Promise((resolve) => {
    if (!url || typeof url !== 'string') {
      resolve({ width: 0, height: 0 });
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () =>
      resolve({ width: img.naturalWidth || 0, height: img.naturalHeight || 0 });
    img.onerror = () => resolve({ width: 0, height: 0 });
    (async () => {
      if (url.startsWith('data:')) {
        img.src = url;
        return;
      }
      const signed = await resolveSignedDisplayUrl(url);
      img.src = sanitizeManagedDisplayResult(signed, url) || workbenchPreviewUrl(url);
    })();
  });
}

/**
 * 与 ImageCropDetailPage 一致：写入 crop-images，供裁剪详情 / 列表展示。
 */
export async function persistCropImagesModuleRecord(
  taskId,
  imageId,
  originalImageUrl,
  croppedCanonicalUrl,
  cropStatus = 'success',
) {
  const oid = String(imageId || '').trim();
  if (!taskId || !oid || !croppedCanonicalUrl) return;
  const orig = String(originalImageUrl || '').trim();
  const crop = String(croppedCanonicalUrl || '').trim();
  const { width, height } = await loadImageDimensions(crop);
  const st = cropStatus === 'partial' ? 'partial' : 'success';
  try {
    await cropImageAPI.save(taskId, {
      imageId: oid,
      originalImageUrl: orig,
      croppedImageUrl: crop,
      width: width || undefined,
      height: height || undefined,
      status: st,
    });
  } catch (e) {
    console.warn('[workflow] crop-images 保存失败（裁剪模块记录）', taskId, e);
  }
}

/**
 * 与智能抠图详情（AiImageDetailPage / smartCutout）一致：抠图结果写入图片空间 id=cutout_${taskId}_${imageId}，source=smartCutout。
 */
export async function persistSmartCutoutModuleImageSpace(taskId, imageId, cutoutUrl, lingtuMeta = {}) {
  const oid = String(imageId || '').trim();
  const raw = String(cutoutUrl || '').trim();
  if (!taskId || !oid || !raw) return;
  // 从 taskId 中提取条目 ID，生成简短的 record_id（数据库字段长度限制）
  // 例如：cutout-5645 → cutout_5645_xxx
  // 例如：smartCutout-wf-5645-xxx → 提取 5645
  let rowId = '';
  const match = String(taskId).match(/-(\d+)(?:-|$)/);
  if (match) {
    rowId = match[1];
  } else {
    // 兜底：截取 taskId 的最后 8 位数字
    rowId = String(taskId).replace(/\D/g, '').slice(-8);
  }

  // 使用简短的 ID：cutout_{条目ID}_{imageId后6位}
  const oidShort = oid.slice(-6);
  const newId = `cutout_${rowId}_${oidShort}`;
  const name = `智能抠图_${oid}`;
  let dataUrlForSave = '';
  let urlForSave = '';


  if (raw.startsWith('data:image/')) {
    dataUrlForSave = raw;
  } else {
    urlForSave = raw;
  }

  const w = lingtuMeta.width || 1000;
  const h = lingtuMeta.height || 1000;

  try {
    if (dataUrlForSave) {
      let compressed = await compressImageForDatabase(dataUrlForSave, w, h);
      compressed = await ensureImageSizeWithinLimit(compressed, w, h);
      const item = {
        id: newId,
        name,
        url: (compressed && String(compressed).trim()) || '',
        dataUrl: compressed,
        source: 'smartCutout',
        width: lingtuMeta.width || null,
        height: lingtuMeta.height || null,
        size: null,
      };
      const converted = await ensureImageSpaceItemsServerUrl([item]);
      const toSave = converted.filter((i) => i.url && isPersistedImageUrl(i.url));
      if (toSave.length > 0) {
        await imageSpaceAPI.batchSave(toSave);
      }
      await saveImageToIndexedDB(newId, dataUrlForSave, { skipServerUpload: true });
    } else if (urlForSave) {
      const item = {
        id: newId,
        name,
        url: urlForSave,
        dataUrl: null,
        source: 'smartCutout',
        width: lingtuMeta.width || null,
        height: lingtuMeta.height || null,
        size: null,
      };
      const converted = await ensureImageSpaceItemsServerUrl([item]);
      const toSave = converted.filter((i) => i.url && isPersistedImageUrl(i.url));
      if (toSave.length > 0) {
        await imageSpaceAPI.batchSave(toSave);
      }
    }
  } catch (e) {
    // 静默失败，不影响工作流主流程
    console.warn('[workflow] 抠图结果写入图片空间失败（已跳过）', taskId, oid, e?.message);
  }
}
